import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { FormProvider, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Save } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useToast } from '@/components/ui/use-toast'
import { recipeFormSchema } from '@/lib/validations'
import { aiRecipeImage } from '@/services/ai-recipe-image'
import { useHouseholdStore } from '@/stores/household-store'
import { useRecipeStore } from '@/stores/recipe-store'
import type { Recipe, RecipeFormData } from '@/types/recipe'
import { RecipeFormIngredients } from './recipe-form-ingredients'
import { RecipeFormInstructions } from './recipe-form-instructions'
import { RecipeFormMetadata } from './recipe-form-metadata'

interface RecipeFormProps {
  recipe?: Recipe
  mode?: 'create' | 'edit'
}

export function RecipeForm({ recipe, mode = 'create' }: RecipeFormProps) {
  const navigate = useNavigate()
  const { createRecipe, updateRecipe } = useRecipeStore()
  const { currentHousehold, viewMode } = useHouseholdStore()
  const { toast } = useToast()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [imageUploadMode, setImageUploadMode] = useState(false)
  const [generateAiImage, setGenerateAiImage] = useState(false)

  const methods = useForm<RecipeFormData>({
    resolver: zodResolver(recipeFormSchema),
    defaultValues: {
      title: recipe?.title || '',
      description: recipe?.description || '',
      ingredients: recipe?.ingredients || [''],
      instructions: recipe?.instructions || [''],
      prep_time: recipe?.prep_time || undefined,
      cook_time: recipe?.cook_time || undefined,
      servings: recipe?.servings || undefined,
      difficulty: recipe?.difficulty || undefined,
      meal_type: recipe?.meal_type || '',
      tags: recipe?.tags || [],
      equipment: recipe?.equipment || [],
      image_url: recipe?.image_url || '',
      rating: recipe?.rating || undefined,
      notes: recipe?.notes || '',
      is_public: recipe?.is_public ?? false,
    },
  })

  const {
    handleSubmit,
    formState: { errors },
  } = methods

  const onSubmit = async (data: RecipeFormData) => {
    setIsSubmitting(true)

    try {
      const cleanedData: RecipeFormData = {
        title: data.title,
        description: data.description,
        ingredients: data.ingredients.filter(Boolean),
        instructions: data.instructions.filter(Boolean),
        prep_time: data.prep_time || null,
        cook_time: data.cook_time || null,
        servings: data.servings || null,
        difficulty: (data.difficulty || '') as RecipeFormData['difficulty'],
        meal_type: (data.meal_type || '') as RecipeFormData['meal_type'],
        tags: data.tags.filter(Boolean),
        equipment: data.equipment.filter(Boolean),
        image_url: data.image_url || '',
        rating: data.rating || null,
        notes: data.notes || '',
        is_public: Boolean(data.is_public),
      }

      if (mode === 'create' && generateAiImage && !cleanedData.image_url) {
        const imageResult = await aiRecipeImage.generateRecipeImage({
          title: cleanedData.title,
          description: cleanedData.description,
          ingredients: cleanedData.ingredients,
          tags: cleanedData.tags,
          meal_type: cleanedData.meal_type || undefined,
        })
        if (imageResult.success && imageResult.imageUrl) {
          cleanedData.image_url = imageResult.imageUrl
        } else {
          toast({
            title: 'AI-afbeelding overgeslagen',
            description: imageResult.error || 'Het recept is opgeslagen zonder gegenereerde afbeelding.',
            variant: 'destructive',
          })
        }
      }

      if (mode === 'edit' && recipe) {
        await updateRecipe(recipe.id, cleanedData)
      } else {
        await createRecipe(cleanedData, viewMode === 'household' ? currentHousehold?.id : undefined)
      }

      navigate('/recepten')
    } catch (error) {
      console.error('Error saving recipe:', error)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="container max-w-2xl py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">{mode === 'edit' ? 'Recept Bewerken' : 'Nieuw Recept'}</h1>
        <p className="mt-2 text-gray-600">
          {mode === 'edit' ? 'Pas je recept aan naar wens' : 'Voeg een nieuw recept toe aan je collectie'}
        </p>
      </div>

      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
          <RecipeFormMetadata
            mode={mode}
            errors={errors as Record<string, unknown>}
            imageUploadMode={imageUploadMode}
            isSubmitting={isSubmitting}
            onImageUploadModeChange={setImageUploadMode}
            generateAiImage={generateAiImage}
            onGenerateAiImageChange={setGenerateAiImage}
          />

          <RecipeFormIngredients />
          <RecipeFormInstructions />

          <div className="flex gap-4">
            <Button type="submit" disabled={isSubmitting} size="lg" className="flex-1">
              <Save className="mr-2 h-5 w-5" />
              {isSubmitting ? 'Opslaan...' : mode === 'edit' ? 'Wijzigingen Opslaan' : 'Recept Opslaan'}
            </Button>

            <Button type="button" variant="outline" size="lg" onClick={() => navigate(-1)}>
              Annuleren
            </Button>
          </div>
        </form>
      </FormProvider>
    </div>
  )
}
